/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import { BaseModel } from '../base.model';
import {
  CertificationModel,
  ProspectDataFileDto,
  ProspectFitModel,
  ProspectResumeModel,
  SafetyModel,
} from '../sparta-api';

interface LegacyDatepickerObject {
  date: {
    year: number;
    month: number;
    day: number;
  };
}

type LegacyDatepickerValue = LegacyDatepickerObject | Date | string | null;

function toDatepickerDate(value: LegacyDatepickerValue): Date | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'object' && 'date' in value) {
    return new Date(value.date.year, value.date.month - 1, value.date.day);
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export class ProspectModel extends BaseModel {
  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  _dateOfBirthBackingStore: LegacyDatepickerValue = null;
  _tWICCardExpirationDateBackingStore: LegacyDatepickerValue = null;
  _licenseExpirationBackingStore: LegacyDatepickerValue = null;
  _lastContactedDateBackingStore: LegacyDatepickerValue = null;
  _lastCheckinDateBackingStore: LegacyDatepickerValue = null;

  ProspectID: string;
  Contact = '';
  FirstName = '';
  LastName = '';
  MiddleName = '';
  Nickname = '';
  PhoneNumber = '';
  CellPhone = '';
  AlternatePhoneNumber = '';
  Email = '';
  SSN = '';
  StreetAddress = '';
  StreetAddress2 = '';
  City = '';
  State = '';
  Country = '';
  ZipCode = '';
  Notes = '';
  Suffix = '';
  CompanyID = '';
  VPCompanyID = null;
  LicenseTypeID: number | null;
  LicenseTypeName = '';
  LicenseNumber = '';
  PhysicalStreetAddress = '';
  PhysicalAddress2 = '';
  PhysicalCity = '';
  PhysicalState = '';
  PhysicalCountry = '';
  PhysicalZipCode = '';
  TWICCardNumber = '';
  IsActive = false;
  Ranking = null;
  bFormerEmployee = false;
  ClaimedTwic = false;
  ClaimedCerts = '';
  CommunicationPreference = '';
  TradeGroupIDs: string[] = [];
  TradeClassIDs: string[] = [];
  bContacted = false;
  bCheckin = false;
  CheckinNotes = '';
  PersonNotes = '';
  AbsorbDepartmentId = '';

  LocalUnionName = '';
  LocalUnionNumber = '';
  LocalUnionGrade = 0;
  LocalUnionYrsOfExperience = 0;

  SavedCellPhone = '';
  certifications: CertificationModel[] = [];
  safetyItems: SafetyModel[] = [];
  fitItems: ProspectFitModel[] = [];
  resumes: ProspectResumeModel[] = [];

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  get DateOfBirth(): LegacyDatepickerValue {
    return this._dateOfBirthBackingStore;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  set DateOfBirth(x: LegacyDatepickerValue) {
    this.Birthday = toDatepickerDate(x);
    this._dateOfBirthBackingStore = x;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  get TWICCardExpirationDate(): LegacyDatepickerValue {
    return this._tWICCardExpirationDateBackingStore;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  set TWICCardExpirationDate(x: LegacyDatepickerValue) {
    this.TWICCardExpiration = toDatepickerDate(x);
    this._tWICCardExpirationDateBackingStore = x;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  get LicenseExpiration(): LegacyDatepickerValue {
    return this._licenseExpirationBackingStore;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  set LicenseExpiration(x: LegacyDatepickerValue) {
    this.LicenseExpirationDate = toDatepickerDate(x);
    this._licenseExpirationBackingStore = x;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  get LastContacted(): LegacyDatepickerValue {
    return this._lastContactedDateBackingStore;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  set LastContacted(x: LegacyDatepickerValue) {
    this.LastContactedDate = toDatepickerDate(x);
    this._lastContactedDateBackingStore = x;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  get LastCheckin(): LegacyDatepickerValue {
    return this._lastCheckinDateBackingStore;
  }

  // Remove the ANY type once we replace the my-datepicker with kendo-datepicker - as the my-datepicker mixes the formats
  set LastCheckin(x: LegacyDatepickerValue) {
    this.LastCheckinDate = toDatepickerDate(x);
    this._lastCheckinDateBackingStore = x;
  }

  Birthday: Date | null;
  BirthDateString = '';
  TWICCardExpiration: Date;
  LicenseExpirationDate: Date;
  LastContactedDate: Date;
  LastCheckinDate: Date;

  constructor(o: unknown = null) {
    super(o);
  }

  ApplicationStatus: string;
  Files: ProspectDataFileDto[];
  Position: string;
  CompanyName: string;
  bExternalGuest: boolean;
}
